import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {adjustFromTimeZoneSafely} from "../date/date";

export const CustomFieldProfileCodec = t.intersection([
  t.type({
    id: t.string,
    companyId: t.string,
    forObjectType: t.string,
    name: t.string,
    effectiveFromDttm: t.number,
  }),
  t.partial({
    description: t.string,
    effectiveToDttm: t.number,
    createdById: t.string,
    lastModifiedById: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,

    companyName: t.string,
  }),
]);

export type CustomFieldProfile = t.TypeOf<typeof CustomFieldProfileCodec>;

@Exclude()
export class CustomFieldProfileEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  companyId!: string;

  @Expose()
  forObjectType!: string;

  @Expose()
  name!: string;

  @Expose()
  effectiveFromDttm!: number;

  // ─────────────────────────────
  // Optional fields
  // ─────────────────────────────

  @Expose()
  description?: string;

  @Expose()
  effectiveToDttm?: number;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  companyName?: string;

  readonly entityType = "CUSTOMFIELDPROFILE";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for CustomFieldProfileEntity. Recommend using the {@link from} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: CustomFieldProfile): CustomFieldProfileEntity {
    return plainToInstance(CustomFieldProfileEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<CustomFieldProfile> {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSON(CustomFieldProfileCodec, object);
  }

  asTypeValue(): CustomFieldProfile {
    const object = instanceToPlain(this, {excludeExtraneousValues: true});
    return validateJSONNow(CustomFieldProfileCodec, object);
  }

  isActive = (dttm: number = new Date().getTime()): boolean => {
    const adjustedStartDttm = adjustFromTimeZoneSafely(this.effectiveFromDttm)?.getTime()
    const adjustedEndDttm = adjustFromTimeZoneSafely(this.effectiveToDttm)?.getTime()

    const meetsStartThresh = adjustedStartDttm == null || adjustedStartDttm <= dttm
    const meetsEndThresh = adjustedEndDttm == null || adjustedEndDttm > dttm

    return meetsStartThresh && meetsEndThresh
  }
}

export const CustomFieldProfileCatalogItemCodec = t.intersection([
  t.type({
    defId: t.string,
    defObjectType: t.string,
    code: t.string,
    dataType: t.string,
    defLabel: t.string,
    defRequired: t.boolean,
    selected: t.boolean,
  }),
  t.partial({
    refObjectType: t.string,
    profileItemId: t.string,
    profileItemLabel: t.string,
    profileItemRequired: t.boolean,
    profileItemSortOrder: t.number,
  }),
]);

export type CustomFieldProfileCatalogItem = t.TypeOf<typeof CustomFieldProfileCatalogItemCodec>;
